import * as Sentry from "@sentry/react";
import { browserTracingIntegration, replayIntegration } from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// PII patterns for sanitization
const EMAIL_PATTERN = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/gi;
const PHONE_PATTERN = /\+?\d{10,15}|\(\d{3}\)\s?\d{3}[-.]?\d{4}|\d{3}[-.]?\d{3}[-.]?\d{4}/g;
const TOKEN_PATTERN = /eyJ[A-Za-z0-9_-]*\.eyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*/g;
const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Sanitize PII from a string
 */
function sanitizePII(text: string): string {
  if (typeof text !== 'string') return text;
  return text
    .replace(EMAIL_PATTERN, '[EMAIL_REDACTED]')
    .replace(PHONE_PATTERN, '[PHONE_REDACTED]')
    .replace(TOKEN_PATTERN, '[TOKEN_REDACTED]');
}

/**
 * Recursively sanitize an object
 */
function sanitizeObject(obj: unknown, depth = 0): unknown {
  if (depth > 5) return '[DEPTH_LIMIT]';
  if (obj === null || obj === undefined) return obj;
  
  if (typeof obj === 'string') {
    return sanitizePII(obj);
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, depth + 1));
  }
  
  if (typeof obj === 'object') {
    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      // Redact sensitive keys entirely
      const lowerKey = key.toLowerCase();
      if (['email', 'phone', 'password', 'token', 'secret', 'apikey', 'api_key', 'authorization'].includes(lowerKey)) {
        sanitized[key] = '[REDACTED]';
      } else if (['name', 'firstname', 'lastname', 'first_name', 'last_name'].includes(lowerKey)) {
        sanitized[key] = '[NAME_REDACTED]';
      } else {
        sanitized[key] = sanitizeObject(value, depth + 1);
      }
    }
    return sanitized;
  }
  
  return obj;
}

/**
 * Initialize Sentry for error tracking with PII protection
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn("[Sentry] No DSN configured, error tracking disabled");
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      browserTracingIntegration(),
      replayIntegration(),
    ],
    // Performance monitoring - increased for better coverage
    tracesSampleRate: 0.2,
    // Session replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
    enabled: true,
    
    // Filter noisy errors
    ignoreErrors: [
      /^chrome-extension/,
      /^moz-extension/,
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
      "Failed to fetch",
      "NetworkError",
      "Load failed",
    ],
    
    // CRITICAL: Sanitize all events before sending
    beforeSend(event, hint) {
      // Sanitize error message
      if (event.message) {
        event.message = sanitizePII(event.message);
      }
      
      // Sanitize exception values
      if (event.exception?.values) {
        event.exception.values = event.exception.values.map(ex => ({
          ...ex,
          value: ex.value ? sanitizePII(ex.value) : ex.value,
        }));
      }

      // Tag for alerting
      const error = hint.originalException;
      if (error instanceof Error) {
        if (error.message.includes("payment") || error.message.includes("stripe")) {
          event.tags = { ...event.tags, alert_priority: "high", category: "payment" };
        }
        if (error.message.includes("auth") || error.message.includes("login")) {
          event.tags = { ...event.tags, alert_priority: "medium", category: "auth" };
        }
        if (error.message.includes("supabase") || error.message.includes("database")) {
          event.tags = { ...event.tags, alert_priority: "high", category: "database" };
        }
      }

      // Sanitize breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data?.url) {
            try {
              const url = new URL(breadcrumb.data.url);
              // Remove all query params that might contain sensitive data
              ['token', 'access_token', 'refresh_token', 'email', 'password', 'key', 'secret'].forEach(param => {
                url.searchParams.delete(param);
              });
              breadcrumb.data.url = url.toString();
            } catch {
              // Invalid URL, redact entirely
              breadcrumb.data.url = '[URL_REDACTED]';
            }
          }
          if (breadcrumb.message) {
            breadcrumb.message = sanitizePII(breadcrumb.message);
          }
          if (breadcrumb.data) {
            breadcrumb.data = sanitizeObject(breadcrumb.data) as Record<string, unknown>;
          }
          return breadcrumb;
        });
      }

      // Sanitize contexts
      if (event.contexts) {
        event.contexts = sanitizeObject(event.contexts) as typeof event.contexts;
      }

      // Sanitize extra data
      if (event.extra) {
        event.extra = sanitizeObject(event.extra) as typeof event.extra;
      }

      return event;
    },
  });

  console.log("[Sentry] Initialized with PII protection");
}

/**
 * Capture exception with sanitized context
 */
export function captureException(
  error: Error, 
  context?: Record<string, any>,
  priority: "low" | "medium" | "high" = "medium"
) {
  Sentry.withScope((scope) => {
    scope.setTag("alert_priority", priority);
    if (context) {
      scope.setContext("additional", sanitizeObject(context) as Record<string, unknown>);
    }
    Sentry.captureException(error);
  });
}

/**
 * Capture a message for debugging
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = "info") {
  Sentry.captureMessage(sanitizePII(message), level);
}

/**
 * Capture critical alert
 */
export function captureCriticalAlert(message: string, context?: Record<string, any>) {
  Sentry.withScope((scope) => {
    scope.setTag("alert_priority", "critical");
    scope.setLevel("fatal");
    if (context) {
      scope.setContext("alert_details", sanitizeObject(context) as Record<string, unknown>);
    }
    Sentry.captureMessage(sanitizePII(message), "fatal");
  });
}

/**
 * Set user context - only ID, no PII
 */
export function setUser(user: { id: string; email?: string } | null) {
  if (user) {
    // Only store user ID, never email
    Sentry.setUser({ id: user.id });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add custom tags
 */
export function setTags(tags: Record<string, string>) {
  Object.entries(tags).forEach(([key, value]) => {
    Sentry.setTag(key, sanitizePII(value));
  });
}

export { Sentry };
